/**
 * @desc: Roblox 筛选页 boot 初始化 — 统一 TopCharts / GameRank 的 init 与 loading 逻辑
 */
import { useEffect, useRef, useState } from 'react';
import type { IFilterValues } from '@intelligence/v3/filterCore/types';

export interface IUseRobloxFilterablePageBootOptions<T extends IFilterValues> {
  /** 筛选器 hook 是否就绪（options + 额外依赖如 defaultDate） */
  hookReady: boolean;
  /** 市场筛选 loading，用于 Filter 组件 loading 态 */
  filterLoading: boolean;
  /** 当前 URL search，用于菜单刷新检测 */
  locationSearch: string;
  /** 当前 URL key，用于同页菜单重复点击检测 */
  locationKey?: string;
  getInitialFilterValues: () => Promise<T>;
  /** init 开始前重置页面局部状态 */
  onBeforeInitialize?: () => void;
  /** 拿到初始筛选值后拉取首屏数据 */
  onInitialized: (filterValues: T) => Promise<void> | void;
  logTag?: string;
}

export interface IUseRobloxFilterablePageBootResult {
  isInitialFilterValuesLoading: boolean;
  isPageBootLoading: boolean;
  loading: boolean;
}

export default function useRobloxFilterablePageBoot<T extends IFilterValues>(
  options: IUseRobloxFilterablePageBootOptions<T>,
): IUseRobloxFilterablePageBootResult {
  const {
    hookReady,
    filterLoading,
    locationSearch,
    locationKey,
    getInitialFilterValues,
    onBeforeInitialize,
    onInitialized,
    logTag = 'RobloxPage',
  } = options;

  const prevLocationSearchRef = useRef<string | undefined>(undefined);
  const prevLocationKeyRef = useRef<string | undefined>(undefined);
  const hasInitializedRef = useRef(false);
  const initRequestIdRef = useRef(0);
  const onBeforeInitializeRef = useRef(onBeforeInitialize);
  const onInitializedRef = useRef(onInitialized);
  const getInitialFilterValuesRef = useRef(getInitialFilterValues);

  onBeforeInitializeRef.current = onBeforeInitialize;
  onInitializedRef.current = onInitialized;
  getInitialFilterValuesRef.current = getInitialFilterValues;

  const [isInitialFilterValuesLoading, setIsInitialFilterValuesLoading] = useState(true);

  useEffect(() => {
    const currentSearch = locationSearch;
    const prevSearch = prevLocationSearchRef.current;
    prevLocationSearchRef.current = currentSearch;

    const currentKey = locationKey;
    const prevKey = prevLocationKeyRef.current;
    prevLocationKeyRef.current = currentKey;

    const isSearchClearedRefresh = Boolean(prevSearch) && !currentSearch;
    const isSamePageReentry = Boolean(
      prevKey &&
      currentKey &&
      prevKey !== currentKey &&
      !currentSearch,
    );
    const shouldInitialize = hookReady && (
      !hasInitializedRef.current || isSearchClearedRefresh || isSamePageReentry
    );

    if (!hookReady || !shouldInitialize) {
      return undefined;
    }

    hasInitializedRef.current = true;
    const requestId = ++initRequestIdRef.current;
    setIsInitialFilterValuesLoading(true);
    onBeforeInitializeRef.current?.();

    getInitialFilterValuesRef.current()
      .then((nextFilterValues) => {
        if (requestId !== initRequestIdRef.current) {
          return undefined;
        }

        return onInitializedRef.current(nextFilterValues);
      })
      .catch((error) => {
        console.debug(`[${logTag}] initialize failed`, error);
      })
      .finally(() => {
        if (requestId === initRequestIdRef.current) {
          setIsInitialFilterValuesLoading(false);
        }
      });

    return undefined;
    // getInitialFilterValues / onInitialized 每次渲染都会变，不能放进依赖
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hookReady, locationSearch, locationKey, logTag]);

  const isPageBootLoading = !hookReady || isInitialFilterValuesLoading;
  const loading = filterLoading || isPageBootLoading;

  return {
    isInitialFilterValuesLoading,
    isPageBootLoading,
    loading,
  };
}
